import React from 'react';
import { AnchoredPortal } from './AnchoredPortal';
import { COMPOSER_EMOJI_CATEGORIES } from './composerEmojis';
import type { FormatCommand } from './richTextFormat';
import type { ComposerMentionCandidate } from './RichTextComposer';

type BottomPanel = 'plus' | 'emoji' | 'mention' | null;
type ComposerEmoji = { char: string; keywords: string; categoryId?: string };

export interface RichTextBottomChromeProps {
  showBottomExtras: boolean;
  showToolbarToggle: boolean;
  disabled: boolean;
  toolbarVisible: boolean;
  bottomPanel: BottomPanel;
  testId?: string;
  plusTriggerRef: React.RefObject<HTMLButtonElement | null>;
  emojiTriggerRef: React.RefObject<HTMLButtonElement | null>;
  mentionTriggerRef: React.RefObject<HTMLButtonElement | null>;
  emojiQuery: string;
  emojiSearchActive: boolean;
  emojiCategoryId: string;
  visibleEmojis: ComposerEmoji[];
  mentionQuery: string;
  filteredMentions: ComposerMentionCandidate[];
  onToggleToolbar: () => void;
  onTogglePanel: (panel: Exclude<BottomPanel, null>) => void;
  onClosePanels: () => void;
  onSetLink: () => void;
  onRunFormat: (command: FormatCommand) => void;
  onEmojiQuery: (value: string) => void;
  onEmojiCategory: (id: string) => void;
  onInsertEmoji: (emoji: string) => void;
  onMentionQuery: (value: string) => void;
  onInsertMention: (candidate: ComposerMentionCandidate) => void;
  onMentionCandidateUnavailable?: (candidate: ComposerMentionCandidate) => void;
}

function PlusMenu({
  open,
  anchorRef,
  testId,
  onClose,
  onSetLink,
  onRunFormat,
}: {
  open: boolean;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  testId?: string;
  onClose: () => void;
  onSetLink: () => void;
  onRunFormat: (command: FormatCommand) => void;
}) {
  return (
    <AnchoredPortal
      open={open}
      anchorRef={anchorRef}
      onClose={onClose}
      className="z-rtc-popover z-rtc-popover--portal"
      role="menu"
      aria-label="Insertar"
      data-testid={testId ? `${testId}-plus-popover` : undefined}
      estimatedWidth={200}
      estimatedHeight={140}
      preferAbove
    >
      <div className="z-rtc-menu">
        <button type="button" className="z-rtc-menu-item" role="menuitem" onClick={onSetLink}>
          Insertar enlace
        </button>
        <button
          type="button"
          className="z-rtc-menu-item"
          role="menuitem"
          onClick={() => {
            onRunFormat('codeBlock');
            onClose();
          }}
        >
          Bloque de código
        </button>
        <button
          type="button"
          className="z-rtc-menu-item"
          role="menuitem"
          onClick={() => {
            onRunFormat('blockquote');
            onClose();
          }}
        >
          Cita
        </button>
      </div>
    </AnchoredPortal>
  );
}

function EmojiPanel({
  open,
  anchorRef,
  testId,
  emojiQuery,
  emojiSearchActive,
  emojiCategoryId,
  visibleEmojis,
  onClose,
  onEmojiQuery,
  onEmojiCategory,
  onInsertEmoji,
}: {
  open: boolean;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  testId?: string;
  emojiQuery: string;
  emojiSearchActive: boolean;
  emojiCategoryId: string;
  visibleEmojis: ComposerEmoji[];
  onClose: () => void;
  onEmojiQuery: (value: string) => void;
  onEmojiCategory: (id: string) => void;
  onInsertEmoji: (emoji: string) => void;
}) {
  return (
    <AnchoredPortal
      open={open}
      anchorRef={anchorRef}
      onClose={onClose}
      className="z-rtc-popover z-rtc-popover--portal z-rtc-popover--emoji"
      role="dialog"
      aria-label="Emojis"
      data-testid={testId ? `${testId}-emoji-popover` : undefined}
      estimatedWidth={320}
      estimatedHeight={340}
      preferAbove
    >
      <div className="z-rtc-emoji-panel">
        <input
          type="search"
          className="z-rtc-emoji-search"
          placeholder="Buscar emoji…"
          value={emojiQuery}
          onChange={(e) => onEmojiQuery(e.target.value)}
          autoFocus
          aria-label="Filtrar emojis"
          data-testid={testId ? `${testId}-emoji-search` : undefined}
        />
        {!emojiSearchActive ? (
          <div className="z-rtc-emoji-cats" role="tablist" aria-label="Categorías de emoji">
            {COMPOSER_EMOJI_CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                type="button"
                role="tab"
                aria-selected={cat.id === emojiCategoryId}
                aria-label={cat.label}
                title={cat.label}
                className={`z-rtc-emoji-cat ${cat.id === emojiCategoryId ? 'is-active' : ''}`}
                onClick={() => onEmojiCategory(cat.id)}
              >
                {cat.icon}
              </button>
            ))}
          </div>
        ) : null}
        <div className="z-rtc-emoji-grid" role="listbox" aria-label="Lista de emojis">
          {visibleEmojis.length === 0 ? (
            <div className="z-rtc-emoji-empty">Sin coincidencias</div>
          ) : (
            visibleEmojis.map((emoji) => (
              <button
                key={emoji.char}
                type="button"
                className="z-rtc-emoji-btn"
                aria-label={`Insertar ${emoji.char}`}
                title={`Insertar ${emoji.char}`}
                role="option"
                onClick={() => onInsertEmoji(emoji.char)}
              >
                {emoji.char}
              </button>
            ))
          )}
        </div>
      </div>
    </AnchoredPortal>
  );
}

function MentionPanel({
  open,
  anchorRef,
  testId,
  mentionQuery,
  filteredMentions,
  onClose,
  onMentionQuery,
  onInsertMention,
  onMentionCandidateUnavailable,
}: {
  open: boolean;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  testId?: string;
  mentionQuery: string;
  filteredMentions: ComposerMentionCandidate[];
  onClose: () => void;
  onMentionQuery: (value: string) => void;
  onInsertMention: (candidate: ComposerMentionCandidate) => void;
  onMentionCandidateUnavailable?: (candidate: ComposerMentionCandidate) => void;
}) {
  return (
    <AnchoredPortal
      open={open}
      anchorRef={anchorRef}
      onClose={onClose}
      className="z-rtc-popover z-rtc-popover--portal z-rtc-popover--mention"
      role="dialog"
      aria-label="Menciones"
      data-testid={testId ? `${testId}-mention-popover` : undefined}
      estimatedWidth={260}
      estimatedHeight={240}
      preferAbove
    >
      <div className="z-rtc-mention-panel">
        <input
          type="search"
          className="z-rtc-mention-search"
          placeholder="Buscar persona…"
          value={mentionQuery}
          onChange={(e) => onMentionQuery(e.target.value)}
          autoFocus
          aria-label="Filtrar menciones"
        />
        <div role="listbox" aria-label="Candidatos de mención">
          {filteredMentions.length === 0 ? (
            <div className="z-rtc-mention-empty">Sin coincidencias</div>
          ) : (
            filteredMentions.map((candidate) => (
              <button
                key={candidate.id}
                type="button"
                className="z-rtc-menu-item"
                role="option"
                aria-disabled={candidate.disabled || undefined}
                disabled={candidate.disabled && !onMentionCandidateUnavailable}
                title={candidate.disabledReason}
                onClick={() => {
                  if (candidate.disabled) onMentionCandidateUnavailable?.(candidate);
                  else onInsertMention(candidate);
                }}
              >
                <span>@{candidate.label}</span>
                {candidate.aliasLabel ? (
                  <span className="z-rtc-mention-destination">{candidate.aliasLabel}</span>
                ) : null}
                {candidate.disabled && onMentionCandidateUnavailable ? (
                  <span className="z-rtc-mention-destination">Configurar</span>
                ) : null}
              </button>
            ))
          )}
        </div>
      </div>
    </AnchoredPortal>
  );
}

export const RichTextBottomChrome: React.FC<RichTextBottomChromeProps> = (props) => {
  const { showBottomExtras, showToolbarToggle, disabled, toolbarVisible, bottomPanel, testId } =
    props;
  return (
    <div className="z-rtc-bottom">
      <div className="z-rtc-bottom-left">
        {showBottomExtras ? (
          <>
            <button
              ref={props.plusTriggerRef as React.Ref<HTMLButtonElement>}
              type="button"
              className={`z-rtc-bottom-btn ${bottomPanel === 'plus' ? 'is-active' : ''}`}
              title="Más"
              aria-label="Más"
              aria-expanded={bottomPanel === 'plus'}
              aria-haspopup="menu"
              data-testid={testId ? `${testId}-plus` : undefined}
              disabled={disabled}
              onClick={() => props.onTogglePanel('plus')}
            >
              +
            </button>
            <PlusMenu
              open={bottomPanel === 'plus'}
              anchorRef={props.plusTriggerRef}
              testId={testId}
              onClose={props.onClosePanels}
              onSetLink={props.onSetLink}
              onRunFormat={props.onRunFormat}
            />
          </>
        ) : null}
        {showToolbarToggle ? (
          <button
            type="button"
            className={`z-rtc-bottom-btn ${toolbarVisible ? 'is-active' : ''}`}
            title={toolbarVisible ? 'Ocultar formato' : 'Mostrar formato'}
            aria-label={toolbarVisible ? 'Ocultar formato' : 'Mostrar formato'}
            aria-pressed={toolbarVisible}
            data-testid={testId ? `${testId}-aa` : undefined}
            disabled={disabled}
            onClick={props.onToggleToolbar}
          >
            Aa
          </button>
        ) : null}
        {showBottomExtras ? (
          <>
            <button
              ref={props.emojiTriggerRef as React.Ref<HTMLButtonElement>}
              type="button"
              className={`z-rtc-bottom-btn ${bottomPanel === 'emoji' ? 'is-active' : ''}`}
              title="Emoji"
              aria-label="Emoji"
              aria-expanded={bottomPanel === 'emoji'}
              aria-haspopup="dialog"
              data-testid={testId ? `${testId}-emoji` : undefined}
              disabled={disabled}
              onClick={() => props.onTogglePanel('emoji')}
            >
              ☺
            </button>
            <EmojiPanel
              open={bottomPanel === 'emoji'}
              anchorRef={props.emojiTriggerRef}
              testId={testId}
              emojiQuery={props.emojiQuery}
              emojiSearchActive={props.emojiSearchActive}
              emojiCategoryId={props.emojiCategoryId}
              visibleEmojis={props.visibleEmojis}
              onClose={props.onClosePanels}
              onEmojiQuery={props.onEmojiQuery}
              onEmojiCategory={props.onEmojiCategory}
              onInsertEmoji={props.onInsertEmoji}
            />
            <button
              ref={props.mentionTriggerRef as React.Ref<HTMLButtonElement>}
              type="button"
              className={`z-rtc-bottom-btn ${bottomPanel === 'mention' ? 'is-active' : ''}`}
              title="Mencionar"
              aria-label="Mencionar"
              aria-expanded={bottomPanel === 'mention'}
              aria-haspopup="dialog"
              data-testid={testId ? `${testId}-mention` : undefined}
              disabled={disabled}
              onClick={() => props.onTogglePanel('mention')}
            >
              @
            </button>
            <MentionPanel
              open={bottomPanel === 'mention'}
              anchorRef={props.mentionTriggerRef}
              testId={testId}
              mentionQuery={props.mentionQuery}
              filteredMentions={props.filteredMentions}
              onClose={props.onClosePanels}
              onMentionQuery={props.onMentionQuery}
              onInsertMention={props.onInsertMention}
              onMentionCandidateUnavailable={props.onMentionCandidateUnavailable}
            />
          </>
        ) : null}
      </div>
    </div>
  );
};
